import { Heading } from "@sentricmusic/ui";
import { useTranslation } from "react-i18next";
import { FaCheck, FaTimes } from "react-icons/fa";

import { useGetMyWriters, useGetSocieties } from "@/api/queries";
import LoadingSmall from "@/components/LoadingSmall";
import useAppStore from "@/store/appStateZustand";

// getmywork/getmyworkamendment's writersplits entries — narrower than (and not
// interchangeable with) the writersplits shapes returned by the songs table
// or writer-detail endpoints.
export type SongWriterSplitEntry = {
  writercode: string;
  writerdesignationcode?: string;
  isdeleted?: boolean;
  firstname?: string;
  middlename?: string;
  lastname?: string;
  writershare?: number;
  righttocollect?: boolean;
};

type SongWriterSplitProps = {
  writer: SongWriterSplitEntry;
  last: boolean;
  aac?: string | null;
};

function SongWriterSplit({ writer, last, aac = null }: SongWriterSplitProps) {
  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const writers = useGetMyWriters({ aac: aac != null && aac !== "" ? aac : selectedAccount });
  const { t } = useTranslation("songs");
  const societies = useGetSocieties();

  const designationCodes = [
    { name: t("songs:author-option"), value: 1 },
    { name: t("songs:composer-option"), value: 2 },
    { name: t("songs:both-option"), value: 3 },
  ];

  if (!writers.isSuccess || !societies.isSuccess) {
    return (<LoadingSmall />);
  }

  const writerDetails = writers.data.writers.find(w => w.writercode === writer.writercode);

  const society = societies.data.societies.find(s => s.cisaccode === writerDetails?.societycisaccode);

  const writerType = designationCodes.find(d => d.value === Number.parseInt(writer.writerdesignationcode ?? "", 10));

  if (writer.isdeleted === true) {
    return <></>;
  }

  function name(type: "firstname" | "middlename" | "lastname"): string {
    const fromWriterDetails = writerDetails?.[type];
    if (fromWriterDetails != null && fromWriterDetails !== "")
      return fromWriterDetails;

    const fromWriter = writer[type];
    if (fromWriter != null && fromWriter !== "")
      return fromWriter;

    return "";
  }

  return (
    <div className={`flex items-end py-2  ${last ? "" : "border-b border-stroke-default"} `}>
      <div className="flex flex-col w-full pr-2 lg:w-1/2">
        <Heading as="h4">
          {" "}
          {name("firstname")}
          {" "}
          {name("middlename")}
          {" "}
          {name("lastname")}
        </Heading>
        <div className="flex flex-col gap-2 text-xs text-ink-secondary sm:gap-0">
          <p>
            {t("songs:membership")}
            :
            {" "}
            {society?.name}
          </p>
          <p>
            CAE/IPI #:
            {" "}
            {writerDetails?.caeipinumber != null && writerDetails.caeipinumber !== "" ? writerDetails.caeipinumber : "-"}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-end w-full mt-4 lg:w-1/2 lg:mt-0">
        <div className="w-full xl:w-1/3">
          <p className="text-xs font-semibold text-ink-primary">
            {writerType?.name}
          </p>
        </div>
        <div className="w-full xl:w-1/3">
          <p className="text-xs font-semibold text-ink-primary">
            {writer.writershare}
            %
            {" "}
            {t("songs:split")}
          </p>
        </div>
        <div className="w-full xl:w-1/3">
          <p className="flex items-start gap-2 font-semibold text-xs">
            {writer.righttocollect
              ? (
                  <>
                    <FaCheck className="mt-0.5 shrink-0" style={{ color: "var(--color-stroke-success)" }} />
                    <span style={{ color: "var(--color-stroke-success)" }}>{t("songs:right-to-collect")}</span>
                  </>
                )
              : (
                  <>
                    <FaTimes className="mt-0.5 shrink-0" style={{ color: "var(--color-stroke-error)" }} />
                    <span style={{ color: "var(--color-stroke-error)" }}>{t("songs:no-right-to-collect")}</span>
                  </>
                )}
          </p>
        </div>
      </div>

    </div>
  );
}

export default SongWriterSplit;
